import { MemoryRouter, Route, Routes, useParams } from 'react-router-dom';
import { errorMessage } from '@/lib/api';
import { useCanvasContext } from '@/embed/context';
import { EmbedMessage } from '@/embed/EmbedMessage';
import { ContractsTab } from '@/pages/event-contracts-tab';
import { ContractTextPage } from '@/pages/event-contract-text';
import { ContractPreviewPage } from '@/pages/event-contract-preview';
import { ContractCountersignPage } from '@/pages/event-contract-countersign';
import type { EmbedWidgetProps } from '@/embed/types';

/** The contracts list + generate, rendered at the router base for the event. */
function ContractHome(): React.ReactElement {
  const { eventId } = useParams<{ eventId: string }>();
  if (!eventId) return <EmbedMessage tone="error" text="Missing event." />;
  return (
    <div className="p-3">
      <ContractsTab subject={{ kind: 'event', id: eventId }} />
    </div>
  );
}

/**
 * The dedicated Contract tab: the **full** contract workflow, inline in
 * Salesforce — generate or upload a draft, edit it in the builder, run the
 * client-review/redline loop, and send for signature — without leaving the
 * record. It reuses the exact admin-app contract pages, wired through a scoped
 * in-memory router (the record page has no browser router of its own), so there
 * is one implementation of contracting, surfaced in two places.
 *
 * Place this on its own Opportunity tab with a tall height (~760px), since the
 * editor needs room; the Overview widget stays a compact status summary.
 *
 * @param props - The Salesforce Opportunity record id.
 * @returns The inline contract workflow.
 */
export function OpportunityContract({ externalId }: EmbedWidgetProps): React.ReactElement {
  const ctx = useCanvasContext('Opportunity', externalId);

  if (ctx.isPending) return <EmbedMessage text="Loading contracts…" />;
  if (ctx.isError) {
    return <EmbedMessage tone="error" text={`Couldn't load this opportunity. ${errorMessage(ctx.error)}`} />;
  }

  const event = ctx.data.kind === 'opportunity' ? ctx.data.event : null;
  if (!event) {
    return (
      <EmbedMessage text="No bcecloud event exists for this opportunity yet. It is created when the opportunity is booked and synced — then the contract can be generated here." />
    );
  }

  // The reused pages navigate by the app's own contract paths
  // (/events/:id/contracts/:cid/text, …); a MemoryRouter scoped to this event
  // lets that navigation work inside the iframe, with no browser-URL changes.
  return (
    <MemoryRouter initialEntries={[`/events/${event.id}?tab=contracts`]}>
      <Routes>
        <Route path="/events/:eventId" element={<ContractHome />} />
        <Route path="/events/:eventId/contracts/:contractId/text" element={<ContractTextPage />} />
        <Route path="/events/:eventId/contracts/:contractId/preview" element={<ContractPreviewPage />} />
        <Route
          path="/events/:eventId/contracts/:contractId/countersign"
          element={<ContractCountersignPage />}
        />
      </Routes>
    </MemoryRouter>
  );
}
